// Phase SA-4/5 — Brief §03 A5: Pattern alerts.
//
// 0-3 amber-glow cards surfacing detected patterns about the user's
// decision-making. The cards are the user's own intelligence asset.
//
// Reads from user_betting_profile.pattern_alerts (JSONB array):
//   [{ key, headline, detail, dismissed?, learn_more_url? }, ...]
//
// Rules (brief verbatim):
//   • Min 10 slips before any alerts render (N08). Below 10:
//     show "Pattern detection activates after 10 slips.
//     {10 − N} more needed."
//   • Max 3 active cards (dismissed=false) shown at once.
//   • Each card has Dismiss + Learn More buttons.
//   • First 2 cards free; 3rd Pro-gated — Free user sees blurred
//     preview + "Unlock Pro" CTA.
//   • Empty (10+ slips but zero patterns yet): muted line
//     "No active patterns. Keep building your profile."

import { Link } from "react-router-dom";

import type { PatternAlert } from "../../slips/useBettingProfile.ts";

const MIN_SLIPS_FOR_PATTERNS = 10;
const MAX_VISIBLE_CARDS = 3;
const FREE_PATTERN_SLOTS = 2;

export interface PatternAlertsProps {
  /** Pattern alert array from user_betting_profile.pattern_alerts. */
  alerts: PatternAlert[];
  /** Total slips analyzed — gates the entire section (N08). */
  totalSlips: number;
  /** Pro tier flag — gates the 3rd+ card slot. */
  pro?: boolean;
  /** Optional dismiss handler. Parent persists the dismissal back
   * to user_betting_profile.pattern_alerts. SA-6 wires this. */
  onDismiss?: (key: string) => void;
}

/** Pure derivation — filter out dismissed alerts + cap at
 * MAX_VISIBLE_CARDS. */
export function visibleAlerts(alerts: PatternAlert[]): PatternAlert[] {
  return alerts
    .filter((a) => !a.dismissed)
    .slice(0, MAX_VISIBLE_CARDS);
}

export function PatternAlerts({
  alerts,
  totalSlips,
  pro = false,
  onDismiss,
}: PatternAlertsProps): JSX.Element {
  // N08 gate.
  if (totalSlips < MIN_SLIPS_FOR_PATTERNS) {
    const remaining = MIN_SLIPS_FOR_PATTERNS - totalSlips;
    return (
      <div
        data-testid="sa-patterns-locked"
        style={{
          padding: "12px 14px",
          border: "1px dashed var(--sa-hairline)",
          borderRadius: 4,
          background: "var(--sa-bg-raised)",
          fontFamily: "var(--sa-font-mono)",
          fontSize: 12,
          color: "var(--sa-fg-secondary)",
          letterSpacing: "0.06em",
          lineHeight: 1.55,
        }}
      >
        {`Pattern detection activates after ${MIN_SLIPS_FOR_PATTERNS} slips. ${remaining} more needed.`}
      </div>
    );
  }

  const visible = visibleAlerts(alerts);

  if (visible.length === 0) {
    return (
      <div
        data-testid="sa-patterns-empty"
        style={{
          padding: "12px 14px",
          fontFamily: "var(--sa-font-mono)",
          fontSize: 12,
          color: "var(--sa-fg-muted)",
          letterSpacing: "0.06em",
        }}
      >
        No active patterns. Keep building your profile — SIKA surfaces them
        as your history accumulates.
      </div>
    );
  }

  return (
    <div
      data-testid="sa-patterns"
      style={{ display: "flex", flexDirection: "column", gap: 10 }}
    >
      {visible.map((alert, idx) => {
        const isLocked = !pro && idx >= FREE_PATTERN_SLOTS;
        return (
          <PatternCard
            key={alert.key}
            alert={alert}
            locked={isLocked}
            onDismiss={onDismiss}
          />
        );
      })}
    </div>
  );
}

function PatternCard({
  alert,
  locked,
  onDismiss,
}: {
  alert: PatternAlert;
  locked: boolean;
  onDismiss?: (key: string) => void;
}): JSX.Element {
  return (
    <div
      className="sa-pattern-card"
      data-testid={`sa-pattern-${alert.key}`}
      data-locked={locked || undefined}
      style={
        locked
          ? {
              position: "relative",
              overflow: "hidden",
            }
          : undefined
      }
    >
      <div className="sa-pattern-headline">{alert.headline}</div>
      <div
        className="sa-pattern-detail"
        style={
          locked
            ? { filter: "blur(4px)", userSelect: "none" as const }
            : undefined
        }
        aria-hidden={locked ? "true" : undefined}
      >
        {alert.detail}
      </div>
      <div className="sa-pattern-actions">
        {locked ? (
          <Link
            to="/pricing"
            className="sa-pattern-action"
            data-testid={`sa-pattern-${alert.key}-unlock`}
            style={{ color: "var(--sa-accent-saffron)" }}
          >
            Unlock with Pro →
          </Link>
        ) : (
          <>
            {alert.learn_more_url ? (
              <Link
                to={alert.learn_more_url}
                className="sa-pattern-action"
                data-testid={`sa-pattern-${alert.key}-learn`}
              >
                Learn More →
              </Link>
            ) : null}
            {onDismiss ? (
              <button
                type="button"
                className="sa-pattern-action"
                onClick={() => onDismiss(alert.key)}
                data-testid={`sa-pattern-${alert.key}-dismiss`}
              >
                Dismiss
              </button>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}
